import { useEffect, useState, useRef } from "react";
import { classNames } from "@NBCUDTC/reactjs-utility-belt/classnames";

import { Image } from "@/components/atoms/Image";
import { MobileTopNavItem } from "@/components/atoms/MobileTopNavItem";
import PeacockLogo from "@/assets/logos/peacock-375-adult.svg";
import { useScrollPosition } from "@/utils/hooks/UseScrollPosition";

import type { MobileTopNavProps } from "./types";
import { DEFAULT_TOP_NAV } from "./configs";
import styles from "./MobileTopNav.module.scss";

/**
 * Mobile Top Nav component that renders a navigation bar with nav items
 *
 * @param items - Navigation items to display
 * @param className - Additional class names for styling
 * @param attrs - Additional HTML attributes for the nav element
 * @returns the MobileTopNav component
 *
 * @example
 * ```tsx
 * <MobileTopNav items={['Item 1', 'Item 2', 'Item 3']} />
 * ```
 */
const MobileTopNav = ({
  items = DEFAULT_TOP_NAV,
  className,
  scrollContainerRef,
  ...attrs
}: MobileTopNavProps) => {
  const [scrollOpacity, setScrollOpacity] = useState(0);
  const scrollY = useScrollPosition(scrollContainerRef);
  const cn = classNames(styles["mobile-top-nav"], className);
  const navBackground = `rgba(0, 0, 0, ${scrollOpacity * 0.8})`;
  const navRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const navHeight = navRef.current?.offsetHeight || 56;
    const opacity = Math.min(scrollY / navHeight, 1);
    setScrollOpacity(opacity);
  }, [scrollY]);

  return (
    <nav
      className={cn}
      style={{ background: navBackground }}
      ref={navRef}
      role="navigation"
      aria-label="Top navigation"
      {...attrs}
    >
      <Image
        src={PeacockLogo}
        allowColorOverride={false}
        className={styles["logo"]}
        alt="Peacock Logo"
      />
      <div className={styles["nav-items"]}>
        {items.map((item, index) => (
          <MobileTopNavItem
            key={item}
            label={item}
            dropdown={index === items.length - 1}
          />
        ))}
      </div>
    </nav>
  );
};

export { MobileTopNav };
